import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  BookmarkIcon,
} from '@heroicons/react/24/outline';
import { savedSearchesAPI, SavedSearch } from '../../services/api';
import { Modal, Button } from '../ui';

interface AdvancedSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSearch: (criteria: any) => void;
  initialCriteria?: any;
}

const AdvancedSearchModal: React.FC<AdvancedSearchModalProps> = ({
  isOpen,
  onClose,
  onSearch,
  initialCriteria = {},
}) => {
  const [criteria, setCriteria] = useState<any>(initialCriteria);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [searchDescription, setSearchDescription] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadSavedSearches();
    }
  }, [isOpen]);

  const loadSavedSearches = async () => {
    try {
      const searches = await savedSearchesAPI.getAll();
      setSavedSearches(searches);
    } catch (error) {
      console.error('Failed to load saved searches:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setCriteria({ ...criteria, [field]: value });
  };

  const handleSearch = () => {
    onSearch(criteria);
    onClose();
  };

  const handleSaveSearch = async () => {
    try {
      await savedSearchesAPI.create({
        name: searchName,
        description: searchDescription,
        criteria,
      });
      setShowSaveDialog(false);
      setSearchName('');
      setSearchDescription('');
      loadSavedSearches();
    } catch (error) {
      console.error('Failed to save search:', error);
    }
  };

  const handleLoadSearch = async (searchId: number) => {
    try {
      const search = await savedSearchesAPI.getById(searchId);
      setCriteria(search.criteria);
    } catch (error) {
      console.error('Failed to load search:', error);
    }
  };

  const handleClear = () => {
    setCriteria({});
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center">
          <FunnelIcon className="h-6 w-6 text-white mr-3" />
          <span>Advanced Search</span>
        </div>
      }
      size="xl"
    >
      {/* Content */}
      <div className="max-h-[60vh] overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Text Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Search Text
            </label>
            <input
              type="text"
              value={criteria.search || ''}
              onChange={(e) => handleInputChange('search', e.target.value)}
              placeholder="Search by name, email, or phone..."
              className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          {/* Client Type */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Client Type
            </label>
            <select
              value={criteria.type || ''}
              onChange={(e) => handleInputChange('type', e.target.value || undefined)}
              className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">All Types</option>
              <option value="ORGANIZATION">Organization</option>
              <option value="COMPANY">Company</option>
              <option value="STORE">Store</option>
              <option value="PERSON">Person</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          {/* Importance */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Importance
            </label>
            <select
              value={criteria.importance || ''}
              onChange={(e) => handleInputChange('importance', e.target.value || undefined)}
              className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">All Levels</option>
              <option value="VERY_LOW">Very Low</option>
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="VERY_HIGH">Very High</option>
            </select>
          </div>

          {/* Lifecycle Stage */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Lifecycle Stage
            </label>
            <select
              value={criteria.lifecycleStage || ''}
              onChange={(e) => handleInputChange('lifecycleStage', e.target.value || undefined)}
              className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">All Stages</option>
              <option value="LEAD">Lead</option>
              <option value="MQL">Marketing Qualified Lead</option>
              <option value="SQL">Sales Qualified Lead</option>
              <option value="OPPORTUNITY">Opportunity</option>
              <option value="CUSTOMER">Customer</option>
              <option value="EVANGELIST">Evangelist</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          {/* Lead Status */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Lead Status
            </label>
            <select
              value={criteria.leadStatus || ''}
              onChange={(e) => handleInputChange('leadStatus', e.target.value || undefined)}
              className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">All Statuses</option>
              <option value="NEW">New</option>
              <option value="CONTACTED">Contacted</option>
              <option value="QUALIFIED">Qualified</option>
              <option value="UNQUALIFIED">Unqualified</option>
              <option value="NURTURING">Nurturing</option>
              <option value="CONVERTED">Converted</option>
              <option value="LOST">Lost</option>
            </select>
          </div>

          {/* Lead Score Range */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Lead Score (Min)
            </label>
            <input
              type="number"
              value={criteria.leadScoreMin || ''}
              onChange={(e) => handleInputChange('leadScoreMin', e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="0"
              className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Lead Score (Max)
            </label>
            <input
              type="number"
              value={criteria.leadScoreMax || ''}
              onChange={(e) => handleInputChange('leadScoreMax', e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="100"
              className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          {/* Industry */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Industry
            </label>
            <input
              type="text"
              value={criteria.industry || ''}
              onChange={(e) => handleInputChange('industry', e.target.value || undefined)}
              placeholder="e.g., Technology, Healthcare, Finance..."
              className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Saved Searches */}
        {savedSearches.length > 0 && (
          <div className="mt-6 pt-6 border-t border-neutral-200">
            <h4 className="text-sm font-medium text-neutral-700 mb-3 flex items-center">
              <BookmarkIcon className="h-5 w-5 mr-2 text-neutral-400" />
              Saved Searches
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {savedSearches.map((search) => (
                <button
                  key={search.id}
                  onClick={() => handleLoadSearch(search.id)}
                  className="text-left px-3 py-2 rounded-lg border border-neutral-200 hover:border-primary-500 hover:bg-primary-50 transition-colors"
                >
                  <div className="font-medium text-sm text-neutral-900">{search.name}</div>
                  {search.description && (
                    <div className="text-xs text-neutral-500 mt-1">{search.description}</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex gap-2">
          <Button variant="ghost" onClick={handleClear}>
            Clear All
          </Button>
          <Button variant="secondary" onClick={() => setShowSaveDialog(true)}>
            Save Search
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSearch}
            leftIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
          >
            Search
          </Button>
        </div>
      </div>

      {/* Save Search Dialog */}
      <Modal
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        title="Save Search"
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Search Name *
            </label>
            <input
              type="text"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              placeholder="e.g., High-value leads"
              className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Description
            </label>
            <textarea
              value={searchDescription}
              onChange={(e) => setSearchDescription(e.target.value)}
              placeholder="Optional description..."
              rows={3}
              className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveSearch}
            disabled={!searchName}
          >
            Save
          </Button>
        </div>
      </Modal>
    </Modal>
  );
};

export default AdvancedSearchModal;

